/*
 * WR-024 Horizon view calendar/geometry helpers, all UTC.
 */

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const DAY_MS = 24 * 60 * 60 * 1000;
const WEEK_MS = 7 * DAY_MS;

/**
 * Parse a day value to a UTC-midnight Date, or null if invalid.
 */
export function parseDay(value) {
  if (value === null || value === undefined || value === '') return null;
  const d = new Date(value);
  if (isNaN(d.getTime())) return null;
  return new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()));
}

/**
 * Build week column objects for the view.
 */
export function weekColumns(anchor, weeks) {
  const anchorDay = parseDay(anchor);
  const anchorYear = anchorDay.getUTCFullYear();
  const anchorMonth = anchorDay.getUTCMonth();
  const anchorDate = anchorDay.getUTCDate();
  const cols = [];
  let prevMonth = -1;
  let prevYear = -1;
  for (let w = 1; w <= weeks; w++) {
    const monday = new Date(Date.UTC(anchorYear, anchorMonth, anchorDate) + (w - 1) * WEEK_MS);
    const month = monday.getUTCMonth();
    const year = monday.getUTCFullYear();
    const date = monday.getUTCDate();
    const label = 'W' + w;
    const isMonthChange = month !== prevMonth || year !== prevYear;
    const day = isMonthChange ? date + ' ' + MONTHS[month] : String(date);
    cols.push({ week: w, monday, label, day });
    prevMonth = month;
    prevYear = year;
  }
  return cols;
}

/**
 * Group consecutive weeks by their Monday's UTC month/year.
 */
export function monthSpans(anchor, weeks) {
  const anchorDay = parseDay(anchor);
  const spans = [];
  let i = 1;
  while (i <= weeks) {
    const weekMonday = new Date(Date.UTC(anchorDay.getUTCFullYear(), anchorDay.getUTCMonth(), anchorDay.getUTCDate()) + (i - 1) * WEEK_MS);
    const year = weekMonday.getUTCFullYear();
    const month = weekMonday.getUTCMonth();
    const monthName = MONTHS[month];
    const first = i;
    let span = 1;
    while (i + span <= weeks) {
      const nextMonday = new Date(Date.UTC(anchorDay.getUTCFullYear(), anchorDay.getUTCMonth(), anchorDay.getUTCDate()) + (i + span - 1) * WEEK_MS);
      if (nextMonday.getUTCFullYear() !== year || nextMonday.getUTCMonth() !== month) break;
      span++;
    }
    const label = month === 0 || (i === 1 && span > 0) ? monthName + ' ' + year : monthName;
    spans.push({ label, first, span });
    i += span;
  }
  return spans;
}

/**
 * The 1-based week containing the UTC day of now.
 */
export function currentWeek(anchor, now) {
  const anchorDay = parseDay(anchor);
  const nowDay = parseDay(now);
  const days = Math.round((nowDay.getTime() - anchorDay.getTime()) / 86400000);
  return Math.floor(days / 7) + 1;
}

/**
 * Weeks from the start of W1 to the middle of today (UTC).
 */
export function todayOffset(anchor, now) {
  const anchorDay = parseDay(anchor);
  const nowDay = parseDay(now);
  const days = Math.round((nowDay.getTime() - anchorDay.getTime()) / 86400000);
  return (days + 0.5) / 7;
}

/**
 * Format the anchor readout for the toolbar.
 */
export function formatAnchor(anchor, weeks) {
  const anchorDay = parseDay(anchor);
  const date = anchorDay.getUTCDate();
  const month = anchorDay.getUTCMonth();
  const year = anchorDay.getUTCFullYear();
  const monthName = MONTHS[month].toUpperCase();
  const weekText = weeks === 1 ? 'WEEK' : 'WEEKS';
  return 'WK 1 = MON ' + date + ' ' + monthName + ' ' + year + ' · ' + weeks + ' ' + weekText;
}

/**
 * Format a window of weeks as range with date range.
 */
export function formatWindow(anchor, start, end) {
  if (start === null || start === undefined || end === null || end === undefined) return '';
  const anchorDay = parseDay(anchor);
  const from = new Date(Date.UTC(anchorDay.getUTCFullYear(), anchorDay.getUTCMonth(), anchorDay.getUTCDate()) + (start - 1) * WEEK_MS);
  const to = new Date(Date.UTC(anchorDay.getUTCFullYear(), anchorDay.getUTCMonth(), anchorDay.getUTCDate()) + (end - 1) * WEEK_MS + 4 * DAY_MS);
  const weeksPart = start === end ? 'W' + start : 'W' + start + ' – W' + end;
  const fromYear = from.getUTCFullYear();
  const toYear = to.getUTCFullYear();
  const d = (x) => x.getUTCDate() + ' ' + MONTHS[x.getUTCMonth()];
  if (fromYear === toYear) {
    return weeksPart + ' · ' + d(from) + ' – ' + d(to) + ' ' + toYear;
  }
  return weeksPart + ' · ' + d(from) + ' ' + fromYear + ' – ' + d(to) + ' ' + toYear;
}

/**
 * Compact weeks range for board cards.
 */
export function formatWeeks(start, end) {
  if (start === null || start === undefined) return '';
  if (start === end) return 'W' + start;
  return 'W' + start + '–' + end;
}

/**
 * Bar box for a week range.
 */
export function barBox(start, end, weekWidth) {
  return { left: (start - 1) * weekWidth + 1, width: (end - start + 1) * weekWidth - 3 };
}

/**
 * X center of a milestone in its week column.
 */
export function milestoneCenter(week, weekWidth) {
  return (week - 1) * weekWidth + weekWidth / 2;
}

/**
 * SVG path and arrow points for a link.
 */
export function linkPath(fromX, fromY, toX, toY) {
  const xv = fromX + 6;
  const xa = toX - 5;
  const d = 'M' + fromX + ' ' + fromY + ' H' + xv + ' V' + toY + ' H' + xa;
  const arrow = xa + ',' + (toY - 4) + ' ' + toX + ',' + toY + ' ' + xa + ',' + (toY + 4);
  return { d, arrow };
}
